import { useState, useEffect, useCallback } from 'react';
import { LogOut, Check } from 'lucide-react';
import { dismissWarning, getAutoLogoutTime } from '../../lib/sessionSecurity';

export function SessionWarningDialog({ open, onLogout }) {
  const [remaining, setRemaining] = useState(300); // 5 minutes in seconds

  useEffect(() => {
    if (!open) {
      setRemaining(300);
      return;
    }

    // Calculate remaining time from the auto-logout deadline
    const targetTime = getAutoLogoutTime();

    const tick = () => {
      const diff = Math.max(0, Math.floor((targetTime - Date.now()) / 1000));
      setRemaining(diff);
      if (diff <= 0 && onLogout) {
        onLogout();
      }
    };

    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [open, onLogout]);

  const handleContinue = useCallback(() => {
    dismissWarning();
  }, []);

  const handleLogout = useCallback(() => {
    if (onLogout) onLogout();
  }, [onLogout]);

  if (!open) return null;

  const hours = Math.floor(remaining / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);
  const seconds = remaining % 60;
  const timeString = `${hours}h:${minutes}m:${seconds}s`;

  return (
    <div
      data-testid="session-warning-overlay"
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
    >
      <div
        data-testid="session-warning-dialog"
        className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
      >
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-brand-navy">Aviso de sesión</h2>
          <button
            onClick={handleLogout}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="session-warning-close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-8 text-center">
          <div className="mb-5">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-soft/30">
              <LogOut className="w-8 h-8 text-brand-navy" />
            </div>
          </div>
          <p className="text-gray-600 text-base mb-4">
            Por seguridad su sesión se cerrará en:
          </p>
          <p
            data-testid="session-warning-countdown"
            className="text-4xl font-bold text-brand-navy tabular-nums tracking-wide"
          >
            {timeString}
          </p>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between gap-3">
          <button
            onClick={handleLogout}
            data-testid="session-warning-logout"
            className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
          >
            Salir
          </button>
          <button
            onClick={handleContinue}
            data-testid="session-warning-continue"
            className="px-6 py-2.5 bg-brand-navy text-white rounded-lg hover:bg-brand-navy transition-colors font-medium flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            Continuar trabajando
          </button>
        </div>
      </div>
    </div>
  );
}
